import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Loading, Panel, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import {
  useConfirmVehicleMutation,
  useGetVeivoPrefillQuery,
  useGetVeivoValidationStatusQuery,
  useMintVeivoResumeTokenMutation,
  useSaveVeivoDraftMutation,
} from "@/redux/apis/vehicle/veivoApi";
import { useAddAvatarMutation, useGetVehicleFormOptionsQuery, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { addToast, capitalize, clsx, formatCurrency } from "@/utils";
import { DetailsStep } from "./steps/DetailsStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { DriversStep } from "./steps/DriversStep";
import { AddingVehicle, ReviewRow, ReviewStep } from "./steps/ReviewStep";
import { WalkaroundStep } from "./steps/WalkaroundStep";
import { FinishLaterModal } from "./FinishLaterModal";
import {
  documentsDone,
  draftChecklist,
  furthestStep,
  NEXT_HINTS,
  initialStep,
  readDraftData,
  V3_STEP_COUNT,
  V3AccessDraft,
  V3DetailsDraft,
  V3DraftData,
  walkaroundDone,
} from "./steps";
import { V3StepIndicator } from "./V3StepIndicator";
import { VehiclePreview, VehiclePreviewData } from "./VehiclePreview";
import { vehicleTitle } from "./VehicleCard";
import type { VeivoSession } from "./useVeivoPanel";

/** While Veivo's steps are open the phone moves things on; this page follows. */
const POLL_MS = 4000;

const STEP_COPY: Record<number, { title: string; description: string }> = {
  1: {
    title: "Upload the vehicle's documents",
    description:
      "Drag files in, or use your phone's camera. Once every document is in and shows what it needs, the walk-around opens.",
  },
  2: { title: "Walk-around and VIN photo", description: "Take your phone to the car and scan the code. It takes about 2 minutes." },
  3: {
    title: "Vehicle details",
    description: "We've filled in what we already know from the plate and documents — check it and add the rest.",
  },
  4: { title: "Who can drive it?", description: "Choose which drivers can take bookings in this vehicle." },
  5: { title: "Review and add the vehicle", description: "Check everything once — the vehicle is added when you confirm." },
};

const bannerFor = (step: number) =>
  step <= 2 ? "Verify this vehicle to activate it for bookings" : step === 5 ? "Checks done ✓ · review and add" : "Checks done ✓ · finish setting up to add this vehicle";

/**
 * The add-vehicle flow on the Veivo path (DEV-1902): Documents → Walk-around →
 * Vehicle details → Drivers → Review, for a verification Find started.
 *
 * The first two steps are Veivo's panels and gates: the server's status decides
 * when each is passed (polled, as the phone moves it on), never the browser.
 * Nothing becomes a vehicle until Review. Everything entered is kept on the
 * draft, so "Save and finish later" resumes here on any device.
 */
export const AddVehicleV3 = () => {
  const { validationId = "" } = useParams<{ validationId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: operatorId, country: operatorCountry, currency } = useOperator();

  const [polling, setPolling] = useState(true);
  const { data: status, isLoading, error, refetch } = useGetVeivoValidationStatusQuery(validationId, {
    skip: !validationId,
    pollingInterval: polling ? POLL_MS : 0,
    refetchOnMountOrArgChange: true,
  });
  const [mintSession] = useMintVeivoResumeTokenMutation();
  const [saveDraft, { isLoading: saving }] = useSaveVeivoDraftMutation();
  const [session, setSession] = useState<VeivoSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [step, setStep] = useState<number | null>(null);
  const [draftData, setDraftData] = useState<V3DraftData>({});
  /** Another photo than the walk-around's front still: uploaded once the
   *  vehicle exists (a file cannot be kept in a draft). */
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null);
  const [adding, setAdding] = useState(false);
  /** "Save and finish later?" open, with what the step had entered. */
  const [finishing, setFinishing] = useState<V3DraftData | null>(null);
  const [confirmError, setConfirmError] = useState<React.ReactNode>(null);
  const [confirmVehicle] = useConfirmVehicleMutation();
  const [addAvatar] = useAddAvatarMutation();
  const { data: formOptions } = useGetVehicleFormOptionsQuery();

  const country = status?.country ?? operatorCountry;
  const { data: statesResponse } = useGetCountryStatesQuery(country, { skip: !country });
  const stateAbbreviation = useMemo(() => {
    const state = statesResponse?.data.states.find((s) => s.id === status?.stateUuid);
    return state?.abbreviation ?? state?.name ?? null;
  }, [statesResponse?.data.states, status?.stateUuid]);

  // A session token for the SDK's panels: short-lived and validation-scoped,
  // minted by v2 so the tenant key never reaches the browser.
  useEffect(() => {
    if (!validationId) return;
    mintSession(validationId)
      .unwrap()
      .then(setSession)
      .catch((e) => setSessionError(getErrorMessages(e)[0] ?? "Could not open this verification."));
  }, [validationId, mintSession]);

  // Open where the operator left it, past any gate passed since — once.
  const opened = useRef(false);
  useEffect(() => {
    if (!status || opened.current) return;
    opened.current = true;
    const saved = readDraftData(status.draft?.data);
    setStep(Math.min(initialStep(status.status, status.draft?.step), furthestStep(status.status, saved)));
    setDraftData(saved);
  }, [status]);

  const furthest = furthestStep(status?.status, draftData);
  const current = step ?? 1;
  const verified = walkaroundDone(status?.status);
  const { data: prefill, isLoading: loadingPrefill } = useGetVeivoPrefillQuery(validationId, { skip: !verified });
  const frontPhoto = prefill?.photos.find((p) => p.orientation === "front")?.url ?? prefill?.photos[0]?.url ?? null;

  // The walk-around passed while the operator watched: on to the details.
  useEffect(() => {
    if (current === 2 && walkaroundDone(status?.status)) setStep(3);
  }, [current, status?.status]);

  // Nothing left for the phone to move on once the verification passed.
  useEffect(() => {
    setPolling(!walkaroundDone(status?.status) && !status?.refusalCode);
  }, [status?.status, status?.refusalCode]);

  const goTo = useCallback((next: number) => setStep(Math.min(Math.max(next, 1), furthest)), [furthest]);

  /** Kept as the operator moves on, so a closed tab loses nothing. */
  const advance = useCallback(
    (data: V3DraftData, next: number) => {
      setDraftData(data);
      setStep(next);
      saveDraft({ validationId, step: next, data: data as Record<string, unknown> })
        .unwrap()
        .catch(() => addToast("info", "Your progress couldn't be saved just now — it will be when you move on."));
    },
    [saveDraft, validationId]
  );

  /** Ask first (DEV-1904): what is saved, what is next. */
  const finishLater = useCallback((data: V3DraftData = draftData) => setFinishing(data), [draftData]);

  const saveAndLeave = useCallback(
    (data: V3DraftData) => {
      saveDraft({ validationId, step: current, data: data as Record<string, unknown> })
        .unwrap()
        .then(() => {
          addToast("success", "Saved. Continue it from Vehicles whenever you're ready.");
          navigate("../..", { relative: "path" });
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    },
    [current, navigate, saveDraft, validationId]
  );

  // The car as the verification knows it: the documents' reading once there
  // is one, Find's description before that — the registry already named the
  // VIN, body and fuel at Find, so the preview shows them from step 1.
  const car = useMemo(() => {
    const summary = status?.vehicleSummary;
    return {
      make: prefill?.make ?? summary?.make ?? null,
      model: prefill?.model ?? summary?.model ?? null,
      year: prefill?.year ?? summary?.year ?? status?.vinDecoded?.modelYear ?? null,
      // Registries and documents shout it ("WHITE").
      colour: capitalize((prefill?.colour ?? summary?.colour ?? "").toLowerCase()) || null,
      fuelType: prefill?.fuelType ?? summary?.fuelType ?? null,
      body: prefill?.body ?? summary?.body ?? null,
      vin: prefill?.vin ?? status?.vin ?? summary?.vin ?? null,
      // NSW's registry discloses only the VIN's tail: shown as such until the
      // registration document gives the whole VIN.
      vinEnding: summary?.vinEnding ?? null,
      seats: prefill?.seats ?? summary?.seats ?? null,
      plate: prefill?.registrationNumber ?? status?.registrationNumber ?? "",
    };
  }, [prefill, status]);

  const preview: VehiclePreviewData = useMemo(
    () => ({
      title: vehicleTitle(car, car.plate || "Vehicle"),
      plateLine: [car.plate, stateAbbreviation].filter(Boolean).join(" · "),
      country,
      rows: [
        { label: "Model year", value: car.year?.toString() },
        // The VIN's decode names the maker ("Toyota Australia") once the
        // registration is read; the registry's make until then.
        { label: "Manufacturer", value: status?.vinDecoded?.wmi?.manufacturer ?? car.make },
        { label: "Body type", value: car.body },
        { label: "Colour", value: car.colour },
        { label: "Engine type", value: car.fuelType },
        { label: "Seats", value: car.seats?.toString() },
        { label: "Country of registration", value: country },
        { label: "VIN", value: car.vin },
        { label: "VIN ending", value: car.vin ? null : car.vinEnding },
      ],
    }),
    [car, country, stateAbbreviation, status?.vinDecoded?.wmi?.manufacturer]
  );

  const fee = formOptions?.fees.verificationFee ?? 0;
  const feeLabel = fee > 0 ? formatCurrency(fee, 2, currency) : null;

  const reviewRows: ReviewRow[] = useMemo(() => {
    const { details, access } = draftData;
    const typeNames = (details?.vehicleTypeUuids ?? [])
      .map((uuid) => formOptions?.vehicleTypes.find((t) => t.uuid === uuid)?.name ?? uuid)
      .join(", ");
    return [
      { label: "Make", value: car.make },
      { label: "Model", value: car.model },
      { label: "Plate Number", value: car.plate },
      { label: "Year", value: car.year?.toString() },
      { label: "Colour", value: car.colour },
      { label: "Engine Type", value: car.fuelType },
      { label: "Passenger Capacity", value: details ? `${details.capacity} pax` : null },
      { label: "Bag Capacity", value: details?.bags?.toString() },
      { label: "State Registered", value: stateAbbreviation },
      { label: "VIN", value: car.vin },
      { label: "Vehicle Type", value: typeNames || null },
      { label: "Vehicle Nickname", value: details?.nickname || null },
      { label: "Vehicle has markings (Signage/Decals)?", value: details ? (details.hasMarkings ? "Yes" : "No") : null },
      { label: "Vehicle Owner Maintenance Fee", value: details?.maintenanceFee ? `${details.maintenanceFee}%` : null },
      {
        label: "Access Level",
        value: access ? (access.level === "ALL" ? "All drivers" : `Restricted (${access.drivers.length} driver${access.drivers.length === 1 ? "" : "s"})`) : null,
      },
      { label: "Verification", value: "Documents, walk-around and VIN photo checked" },
    ];
  }, [car, draftData, formOptions?.vehicleTypes, stateAbbreviation]);

  /**
   * "Add vehicle · $X": web-app creates the vehicle with the details and
   * drivers, charging its fee, then the operator lands on their vehicles with
   * it highlighted. Safe to click again after a failure — the server resumes.
   */
  const handleConfirm = async () => {
    const { details, access } = draftData;
    if (!details || !access) return;
    setConfirmError(null);
    setAdding(true);
    try {
      const { vehicleUuid } = await confirmVehicle({
        validationId,
        details: {
          identifier: details.nickname,
          has_markings: details.hasMarkings,
          capacity_pax: details.capacity,
          capacity_bags: details.bags,
          maintenance_fee: details.maintenanceFee,
          vehicle_types: details.vehicleTypeUuids,
        },
        access: { level: access.level, drivers: access.drivers.map((d) => d.id) },
      }).unwrap();

      if (photo) {
        const body = new FormData();
        body.append("avatar", photo.file);
        await addAvatar({ id: vehicleUuid, body })
          .unwrap()
          .catch(() => addToast("info", "The vehicle was added, but its photo couldn't be changed — change it from the vehicle's page."));
      }
      dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      addToast("success", `${vehicleTitle(car, car.plate)} was added and is ready for bookings.`);
      navigate(`/operator/${operatorId}/vehicles/my-vehicles?added=${encodeURIComponent(vehicleUuid)}`);
    } catch (e) {
      setAdding(false);
      setConfirmError(confirmFailure(e as ConfirmFailure, operatorId, () => goTo(3)));
      // A refusal is the verification's now: the page shows it.
      if ((e as ConfirmFailure)?.data?.code?.startsWith("VEHICLE_ALREADY")) void refetch();
    }
  };

  if (isLoading || (!status && !error)) return <Loading />;
  if (error || !status) {
    return <Alert type="danger" message={getErrorMessages(error)[0] ?? "This verification could not be found."} />;
  }

  // Refused: the car became somebody's vehicle while this was under way.
  if (status.refusalCode) {
    return (
      <Panel className="mx-auto flex max-w-[640px] flex-col gap-4">
        <Typography variant="title">This vehicle can&apos;t be added</Typography>
        <Alert
          type="danger"
          message={
            status.refusalCode === "VEHICLE_ALREADY_IN_FLEET"
              ? "This vehicle is already in your fleet, so it was not added again. You have not been charged."
              : "This vehicle is already registered on RideMinder by another operator, so it can't be added. You have not been charged."
          }
        />
        <Button variant="secondary" size="lg" className="self-start" onClick={() => navigate("../..", { relative: "path" })}>
          Back to vehicles
        </Button>
      </Panel>
    );
  }

  if (adding) return <AddingVehicle />;
  // Until the opening step is decided: never a flash of step 1 for a draft
  // that resumes further on.
  if (step === null) return <Loading />;

  const copy = STEP_COPY[current];
  const isReview = current === V3_STEP_COUNT;

  const renderStep = () => {
    switch (current) {
      case 1:
        return (
          <DocumentsStep
            session={session}
            accepted={documentsDone(status.status)}
            support={status.status === "rejected" ? "rejected" : status.status === "documents_in_review" ? "documents" : null}
            onDocumentsValid={() => void refetch()}
            onContinue={() => goTo(2)}
            onFinishLater={() => finishLater()}
            saving={saving}
          />
        );
      case 2:
        return (
          <WalkaroundStep
            session={session}
            complete={walkaroundDone(status.status)}
            rejected={status.status === "rejected"}
            onComplete={() => void refetch()}
            onBack={() => goTo(1)}
            onNext={() => goTo(3)}
            onFinishLater={() => finishLater()}
            saving={saving}
          />
        );
      case 3:
        // The form takes its defaults once: wait for the suggested capacity.
        if (loadingPrefill) return <Loading />;
        return (
          <DetailsStep
            prefill={prefill}
            saved={draftData.details}
            photoUrl={photo?.url ?? frontPhoto}
            onPhoto={(file) => setPhoto({ file, url: URL.createObjectURL(file) })}
            onBack={() => goTo(2)}
            onNext={(details: V3DetailsDraft) => advance({ ...draftData, details }, 4)}
            onFinishLater={(details: V3DetailsDraft) => finishLater({ ...draftData, details })}
            saving={saving}
          />
        );
      case 4:
        return (
          <DriversStep
            saved={draftData.access}
            onBack={() => goTo(3)}
            onNext={(access: V3AccessDraft) => advance({ ...draftData, access }, 5)}
            onFinishLater={(access: V3AccessDraft) => finishLater({ ...draftData, access })}
            saving={saving}
          />
        );
      case 5:
        return (
          <ReviewStep
            rows={reviewRows}
            photos={prefill?.photos ?? []}
            feeLabel={feeLabel}
            error={confirmError}
            onBack={() => goTo(4)}
            onConfirm={() => void handleConfirm()}
            onFinishLater={() => finishLater()}
            saving={saving}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between rounded-lg bg-info-light px-4 py-2 text-info-dark">
        <Typography variant="paragraph">{bannerFor(current)}</Typography>
        <Typography variant="paragraph">
          Step {current} of {V3_STEP_COUNT}
        </Typography>
      </div>
      {sessionError && <Alert type="danger" message={sessionError} />}

      <div className="flex flex-col-reverse items-start gap-3 xl:flex-row-reverse">
        {!isReview && (
          <Panel className="w-full shrink-0 xl:w-[412px]">
            <VehiclePreview data={preview} />
          </Panel>
        )}
        <Panel className={clsx("flex w-full flex-col", isReview && "mx-auto max-w-[780px]")}>
          <V3StepIndicator current={current} furthest={furthest} onStep={goTo} />
          <div className="mt-6 flex flex-col items-center text-center">
            <Typography variant="title">{copy.title}</Typography>
            <Typography variant="paragraph" className="mt-1 max-w-[560px] text-neutral-dark-gray">
              {copy.description}
            </Typography>
          </div>
          <div className="mt-8">{renderStep()}</div>
        </Panel>
      </div>

      {finishing && (
        <FinishLaterModal
          open
          title={preview.title}
          subtitle={[preview.plateLine, "Draft"].filter(Boolean).join(" · ")}
          items={draftChecklist(status.status, finishing).items}
          nextHint={NEXT_HINTS[draftChecklist(status.status, finishing).next]}
          saving={saving}
          onKeepGoing={() => setFinishing(null)}
          onFinishLater={() => saveAndLeave(finishing)}
        />
      )}
    </div>
  );
};

type ConfirmFailure = { status?: number; data?: { code?: string; message?: string; messages?: Record<string, string[]> | null } };

/** What a refused "Add vehicle" says, and the way on from it. */
function confirmFailure(error: ConfirmFailure, operatorId: string, toDetails: () => void): React.ReactNode {
  const code = error?.data?.code;
  const message = error?.data?.message ?? getErrorMessages(error as Parameters<typeof getErrorMessages>[0])[0];
  if (code === "VEHICLE_LIMIT_REACHED") {
    return (
      <Alert
        type="warning"
        message={
          <span>
            {message}{" "}
            <Link to={`/operator/${operatorId}/payments/subscriptions/subscription-plan`} className="font-semibold underline">
              See plans
            </Link>
          </span>
        }
      />
    );
  }
  if (code === "VEHICLE_DETAILS_INVALID") {
    const details = Object.values(error.data?.messages ?? {}).flat();
    return (
      <Alert
        type="danger"
        message={
          <span>
            {message}
            {details.length > 0 && <span className="block">{details.join(" ")}</span>}{" "}
            <button type="button" className="font-semibold underline" onClick={toDetails}>
              Back to details
            </button>
          </span>
        }
      />
    );
  }
  return <Alert type="danger" message={message ?? "The vehicle couldn't be added. Please try again."} />;
}

